"use client";

import { useLayoutEffect, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { localeCookieName, type Locale } from "@/lib/i18n";

const options: { locale: Locale; label: string; flag: string }[] = [
  { locale: "ru", label: "Русский", flag: "🇷🇺" },
  { locale: "en", label: "English", flag: "🇬🇧" },
  { locale: "ky", label: "Кыргызча", flag: "🇰🇬" },
];

function setLocaleCookie(locale: Locale) {
  const v = encodeURIComponent(locale);
  document.cookie = `${localeCookieName}=${v}; Path=/; Max-Age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

function applyLocale(locale: Locale) {
  setLocaleCookie(locale);
  requestAnimationFrame(() => {
    window.location.assign(window.location.href);
  });
}

export function LanguageMenu({ value }: { value: Locale }) {
  const canPortal = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) {
      setMenuPos(null);
      return;
    }
    const update = () => {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = 190;
      setMenuPos({
        top: r.bottom + 8,
        left: Math.max(8, r.right - width),
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  const current = options.find((o) => o.locale === value) ?? options[0]!;

  const menu =
    open && canPortal && menuPos ? (
      <div
        ref={menuRef}
        className="min-w-[190px] rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 shadow-xl p-2"
        role="menu"
        style={{
          position: "fixed",
          top: menuPos.top,
          left: menuPos.left,
          zIndex: 9999,
        }}
      >
        {options.map((o) => {
          const active = o.locale === value;
          return (
            <button
              key={o.locale}
              type="button"
              role="menuitem"
              className="w-full grid grid-cols-[22px_1fr_auto] items-center gap-3 text-left px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
              aria-current={active ? "true" : undefined}
              onClick={() => {
                if (o.locale === value) {
                  setOpen(false);
                  return;
                }
                setOpen(false);
                applyLocale(o.locale);
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 16 }}>
                {o.flag}
              </span>
              <span style={{ fontWeight: 700 }}>{o.label}</span>
              {active ? <span className="muted">✓</span> : <span />}
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <>
      <div className="langMenu">
        <button
          ref={btnRef}
          type="button"
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span aria-hidden="true" className="text-base">
            {current.flag}
          </span>
        </button>
      </div>
      {menu ? createPortal(menu, document.body) : null}
    </>
  );
}
